import { useState, useCallback } from 'react';
import { mutate } from 'swr';
import { toast } from 'react-toastify';
import api from '../../../services/api';
import {
  examesLabKey,
  viewExamesKey,
  viewLabResultsKey,
  tabelaExamesKey,
  fetchExamesLab,
  fetchViewExames,
  fetchViewLabResults,
  fetchTabelaExames,
} from './examesLaboratoriaisApi';

const EMPTY_LIST = [];
const SWR_WRITE = { revalidate: false };

const applyChartFromResults = (resultados, setChartExames) => {
  if (!resultados || resultados.length === 0) return;
  const labs = resultados.filter(e => e.CODEXAME === resultados[0].CODEXAME);
  const last10 = labs.map(ex => [ex.dataExame, ex.VALOR]).slice(-10);
  last10.unshift(['DATA DO EXAME', resultados[0].EXAME]);
  setChartExames(last10);
};

const applyTableFromTabela = (tabela, setUniqueExames, setTableExames) => {
  if (!tabela || tabela.length === 0) return;
  const labs = tabela.filter(e => e.CODEXAME === tabela[0].CODEXAME);
  const unique = tabela.filter(
    (obj, idx) => idx === tabela.findIndex(o => obj.EXAME === o.EXAME)
  );
  setUniqueExames(unique);
  setTableExames(labs.slice(0, 10));
};

const useExamesLaboratoriais = ({ tipoexame = EMPTY_LIST } = {}) => {
  const [isLoadingExames, setIsLoadingExames] = useState(false);
  const [showExamesLab, setShowExamesLab] = useState(false);
  const [toggleValor, setToggleValor] = useState();
  const [toggleResultado, setToggleResultado] = useState();
  const [showGraficoExamesLab, setShowGraficoExamesLab] = useState(false);
  const [showTabelaExamesLab, setShowTabelaExamesLab] = useState(false);
  const [showTodosExamesLab, setShowTodosExamesLab] = useState(false);
  const [laboratoriais, setLaboratoriais] = useState(EMPTY_LIST);
  const [viewexames, setViewexames] = useState(EMPTY_LIST);
  const [viewlabresults, setViewlabresults] = useState(EMPTY_LIST);
  const [tabelaexames, setTabelaexames] = useState(EMPTY_LIST);
  const [chartExames, setChartExames] = useState(EMPTY_LIST);
  const [tableExames, setTableExames] = useState(EMPTY_LIST);
  const [uniqueExames, setUniqueExames] = useState(EMPTY_LIST);

  const handleNovosExamesLaboratoriais = useCallback(
    async (consulta, dataLabor) => {
      setToggleValor(document.getElementsByName('toggleValor'));
      setToggleResultado(document.getElementsByName('toggleResultado'));

      const listExames = tipoexame.map(tipo => ({
        CODPAC: consulta.CODPAC,
        CODCONSULTA: consulta.CODCONSULTA,
        CODEXAME: tipo.CODEXAME,
        VALOR: 0,
        RESULTADO: '',
        DATALABOR: dataLabor,
      }));

      if (listExames.length === 0) {
        toast.info('Nenhum exame gerado. Consulte a administração!');
        return;
      }

      try {
        setIsLoadingExames(true);
        const response = await api.post(`/listexamelab`, listExames);
        setShowExamesLab(true);

        if (response.status === 207) {
          toast.warning(response.data.message || 'Exames gerados parcialmente!');
        } else {
          toast.success(listExames.length + ' exames gerados com sucesso!');
        }

        try {
          const examesLab = await fetchExamesLab(
            consulta.CODPAC,
            consulta.CODCONSULTA
          );
          setLaboratoriais(examesLab);
          await mutate(
            examesLabKey(consulta.CODPAC, consulta.CODCONSULTA),
            examesLab,
            SWR_WRITE
          );
        } catch (error) {
          toast.error(
            error?.response?.data?.error || 'Erro ao carregar exames!'
          );
        }
      } catch (error) {
        toast.error(error.response?.data?.error || 'Erro ao gerar exames!');
      } finally {
        setIsLoadingExames(false);
      }
    },
    [tipoexame]
  );

  const handleListarExamesLaboratoriais = useCallback(async consulta => {
    try {
      setIsLoadingExames(true);
      setShowExamesLab(true);
      const { CODPAC, CODCONSULTA } = consulta;

      const [viewExamesData, examesLab, resultados, tabela] = await Promise.all(
        [
          fetchViewExames(CODPAC),
          fetchExamesLab(CODPAC, CODCONSULTA),
          fetchViewLabResults(CODPAC),
          fetchTabelaExames(CODPAC),
        ]
      );

      await Promise.all([
        mutate(viewExamesKey(CODPAC), viewExamesData, SWR_WRITE),
        mutate(examesLabKey(CODPAC, CODCONSULTA), examesLab, SWR_WRITE),
        mutate(viewLabResultsKey(CODPAC), resultados, SWR_WRITE),
        mutate(tabelaExamesKey(CODPAC), tabela, SWR_WRITE),
      ]);

      setViewexames(viewExamesData);
      setLaboratoriais(examesLab);
      setViewlabresults(resultados);
      setTabelaexames(tabela);

      applyChartFromResults(resultados, setChartExames);
      if (resultados && resultados.length > 0) {
        setToggleValor(document.getElementsByName('toggleValor'));
        setToggleResultado(document.getElementsByName('toggleResultado'));
      }
      applyTableFromTabela(tabela, setUniqueExames, setTableExames);
    } catch (error) {
      toast.error(
        error?.response?.data?.error || 'Erro ao carregar exames laboratoriais!'
      );
    } finally {
      setIsLoadingExames(false);
    }
  }, []);

  const handleExibirResultadoExames = useCallback(
    exame => {
      if (!viewlabresults) return;

      const labs = viewlabresults.filter(res => res.CODEXAME === exame.CODEXAME);
      const last10Exames = labs.slice(-10).map(ex => [ex.dataExame, ex.VALOR]);
      last10Exames.unshift(['DATA DO EXAME', exame.EXAME]);
      setChartExames(last10Exames);
    },
    [viewlabresults]
  );

  const handleExibirTabelaExames = useCallback(
    exame => {
      if (!tabelaexames) return;

      const exames = tabelaexames.filter(e => e.CODEXAME === exame.CODEXAME);
      const examesUnicos = tabelaexames.filter(
        (obj, idx, arr) => idx === arr.findIndex(o => o.EXAME === obj.EXAME)
      );

      setTableExames(exames.slice(0, 10));
      setUniqueExames(examesUnicos);
    },
    [tabelaexames]
  );

  const handleExcluirZerados = useCallback(async () => {
    try {
      const exames = laboratoriais.filter(
        ex => ex.VALOR !== 0 || ex.RESULTADO !== ''
      );
      const delExames = laboratoriais.filter(lab => !exames.includes(lab));
      setLaboratoriais(exames);
      await api.put(`/examelab`, exames);
      if (delExames.length > 0) {
        await api.delete(`/examelab`, { data: delExames });
      }
      toast.info('Exames Gravados com sucesso!');
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Erro interno!');
    }
  }, [laboratoriais]);

  return {
    isLoadingExames,
    showExamesLab,
    setShowExamesLab,
    showGraficoExamesLab,
    setShowGraficoExamesLab,
    showTabelaExamesLab,
    setShowTabelaExamesLab,
    showTodosExamesLab,
    setShowTodosExamesLab,
    laboratoriais,
    setLaboratoriais,
    viewexames,
    tabelaexames,
    chartExames,
    tableExames,
    uniqueExames,
    toggleValor,
    toggleResultado,
    handleNovosExamesLaboratoriais,
    handleListarExamesLaboratoriais,
    handleExibirResultadoExames,
    handleExibirTabelaExames,
    handleExcluirZerados,
  };
};

export default useExamesLaboratoriais;
